import type { ToolUseItem } from '@heidi/agents/contracts';
import type { ClinicalTaskResultTask } from '@heidi/tasks/contracts';

import { friendlyToolError } from '@heidi/agents/data';
import { ClinicalTaskToolBlock } from './ClinicalTaskToolBlock';
import { isRecord, readString, readToolData, readToolError } from './shared/readers';
import { toClinicalTaskStatus } from './status';

/**
 * Backs `create_clinical_task` and `update_task_status`. Both render the
 * same block with create-flavoured copy ("N tasks created" / "Creating
 * tasks…"). `list_tasks` is row-only.
 *
 * Renders the Projects-scoped `ClinicalTaskToolBlock` (slim agent-todo-style
 * tool row), NOT the legacy shared `ClinicalTaskResultBlock`.
 */
export const renderClinicalTaskToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toClinicalTaskStatus(item.status);
  if (!status) return null;

  // No payload yet — show the animated "creating" loader (tasks unused in
  // that state, but the prop is required).
  if (status === 'creating') {
    return <ClinicalTaskToolBlock status="creating" tasks={[]} />;
  }

  const tasks = readTasks(item);

  if (status === 'failed') {
    return (
      <ClinicalTaskToolBlock
        status="failed"
        tasks={tasks ?? []}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  if (!tasks) return null;
  return <ClinicalTaskToolBlock status="completed" tasks={tasks} />;
};

/**
 * Normalize a completed clinical-task result into the block's `tasks[]`. The
 * three BE tools differ in shape (`provider_metadata.data`):
 *   - `create_clinical_task` → `{ success, tasks[], message }`
 *   - `list_tasks`           → `{ total, count, tasks[] }` (extra per-task fields ignored)
 *   - `update_task_status`   → the whole `data` object IS the task `{ id, title, status, category }`
 * Returns `null` when no valid task can be read so the row falls through to
 * the generic row.
 */
const readTasks = (item: ToolUseItem): ClinicalTaskResultTask[] | null => {
  const data = readToolData(item);
  if (!data) return null;

  // create_clinical_task / list_tasks expose `tasks[]`; update_task_status
  // returns the single updated task as the payload root — wrap it.
  const raw = Array.isArray(data.tasks)
    ? data.tasks
    : readString(data, 'id') && readString(data, 'title')
      ? [data]
      : null;
  if (!raw) return null;

  for (const task of raw) {
    if (!isRecord(task)) return null;
    if (!readString(task, 'id')) return null;
    if (!readString(task, 'title')) return null;
  }

  return raw as ClinicalTaskResultTask[];
};
